/**
 * Platform Purchase Abstraction
 * 
 * Handles purchases differently on web (Stripe) vs native (RevenueCat).
 * Entitlement management is handled server-side.
 */

import { bridge, BridgeChannels, BridgeActions } from '../bridge';
import { api } from '../api';

export type PurchaseResult = 
  | { success: true }
  | { success: false; cancelled: true; error?: never }
  | { success: false; cancelled?: false; error: string };

export interface PurchasePlatform {
  /** 
   * Check if native purchase flow should be used.
   * When true, use purchaseProduct() instead of Stripe payment modal.
   */
  shouldUseNativePurchase(): boolean;

  /**
   * Purchase a specific product by its identifier.
   * @param productId - Product identifier (e.g., "sub_1_month")
   * @param bookingId - Optional booking ID to associate with purchase
   */
  purchaseProduct(productId: string, bookingId?: string): Promise<PurchaseResult>;
}

/**
 * Web implementation - uses Stripe payment modal.
 * The actual Stripe logic is handled by PaymentModal component.
 */
class WebPurchase implements PurchasePlatform {
  shouldUseNativePurchase(): boolean {
    return false;
  }

  async purchaseProduct(_productId: string, _bookingId?: string): Promise<PurchaseResult> {
    // Web doesn't use native purchase - this shouldn't be called
    return { success: false, error: 'Native purchase not available on web' };
  }
}

/**
 * Poll for booking activation after native purchase.
 * The webhook may take a moment to process, so we retry until activated or timeout.
 */
async function pollForBookingActivation(
  bookingId: string,
  maxWaitMs: number = 20000,
  intervalMs: number = 1000
): Promise<boolean> {
  const startTime = Date.now();
  
  while (Date.now() - startTime < maxWaitMs) {
    try {
      const response = await api.post<{ activated: boolean }>(
        `/user/reservations/${bookingId}/activate`
      );
      
      if (response.success && response.data?.activated) {
        console.log('[NativePurchase] Booking activated successfully');
        return true;
      }
      
      console.log('[NativePurchase] Booking not yet activated, retrying...');
    } catch (e) {
      console.error('[NativePurchase] Activation poll error:', e);
    }
    
    // Wait before next attempt
    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }
  
  console.warn('[NativePurchase] Booking activation timed out');
  return false;
}

/**
 * Native implementation - uses RevenueCat via bridge.
 */
class NativePurchase implements PurchasePlatform {
  shouldUseNativePurchase(): boolean {
    return true;
  }

  async purchaseProduct(productId: string, bookingId?: string): Promise<PurchaseResult> {
    try {
      const result = await bridge.request<PurchaseResult>(
        BridgeChannels.PURCHASE,
        BridgeActions.PURCHASE_PRODUCT,
        { productId, bookingId }
      );
      
      // If purchase succeeded and we have a booking, poll for activation
      if (result.success && bookingId) {
        const activated = await pollForBookingActivation(bookingId);
        if (!activated) {
          // Purchase succeeded but booking wasn't activated in time
          // This is still a success - webhook may still process it
          console.warn('[NativePurchase] Purchase succeeded but booking activation timed out');
        }
      }
      
      return result;
    } catch (e) {
      console.error('[NativePurchase] purchaseProduct error:', e);
      return { success: false, error: String(e) };
    }
  }
}

const webPurchase = new WebPurchase();
const nativePurchase = new NativePurchase();

export function getPurchasePlatform(): PurchasePlatform {
  return bridge.isNative ? nativePurchase : webPurchase;
}
